import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import { useTranslation } from "../../../i18n";
import { useTeam } from "../TeamLayout";
import { useTableViewSuppliersQuery } from "../../../__generated__/types-and-hooks";
import { AspectRatio, Button, Breadcrumbs, Link, Input } from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import TableView from "./TableView";
import { useCallback, useEffect, useMemo, useState } from "react";
import HomeIcon from "@mui/icons-material/Home";
import { Link as RouterLink } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import SupplierAddModal from "./SupplierAddModal";
import Fuse from "fuse.js";

export default function Suppliers() {
  const t = useTranslation();
  const teamCtx = useTeam();
  const suppliersListQuery = useTableViewSuppliersQuery({
    variables: { teamId: teamCtx.team.id, limit: 1000 },
  });
  const [modal, setModal] = useState<"add_supplier" | null>(null);
  const handleCreateSupplier = useCallback(() => {
    setModal("add_supplier");
  }, []);

  const suppliers =
    suppliersListQuery.data && suppliersListQuery.data.supplier_list?.suppliers;

  const [search, setSearch] = useState<string | null>(null);
  const [suppliersFiltered, setSuppliersFiltered] = useState<
    typeof suppliers | undefined
  >(undefined);

  const fuse = useMemo(() => {
    if (!suppliers) return null;

    return new Fuse(suppliers, {
      keys: [
        { name: "uniqueId", weight: 1 },
        { name: "name", weight: 0.8 },
        { name: "capabilities", weight: 0.5 },
        { name: "addressLine1", weight: 0.3 },
        { name: "addressLine2", weight: 0.2 },
        { name: "city", weight: 0.1 },
        { name: "state", weight: 0.1 },
        { name: "contryCode", weight: 0.1 },
        { name: "contacts.name", weight: 0.1 },
      ],
      threshold: 0.2, // 0 is exact, 1 is very fuzzy
      includeScore: true,
    });
  }, [suppliers]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSuppliersFiltered(
        fuse && search && search.trim() !== ""
          ? fuse.search(search.trim()).map((result) => result.item)
          : suppliers
      );
    }, 500);

    return () => clearTimeout(handler); // cleanup on every keystroke
  }, [search, suppliers, fuse]);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}`}
        >
          {teamCtx.team.name}
        </Link>
        <Typography level="title-sm">{t("Suppliers")}</Typography>
      </Breadcrumbs>
      <Box sx={{ display: "flex", m: 2 }} gap={1}>
        <Input
          type="text"
          placeholder={t("Search...")}
          startDecorator={<SearchIcon />}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Box sx={{ flex: 1 }}></Box>
        <Button
          startDecorator={<AddIcon />}
          variant="outlined"
          onClick={handleCreateSupplier}
        >
          {t("Add")}
        </Button>
      </Box>
      <Box
        sx={{
          gap: 2,
          px: 2,
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 120px)",
        }}
      >
        {suppliers && suppliers.length === 0 && (
          <>
            <Typography color="neutral" level="body-md">
              {t(
                "Manage all your suppliers in one place to streamline your supply chain operations. Add suppliers manually or connect with integrations like Anvyl or NetSuite."
              )}
              <br />
              <Link
                component={RouterLink}
                to={`/${teamCtx.team.slug}/settings/integrations`}
              >
                {t("Settings/Integrations")}
              </Link>
            </Typography>
            <AspectRatio
              variant="plain"
              ratio={1}
              objectFit="contain"
              maxHeight={"50vh"}
              sx={{ bgcolor: "transparent" }}
            >
              <img
                alt="No suppliers yet"
                src="/images/suppliers_empty_state.png"
              />
            </AspectRatio>
            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <Button
                startDecorator={<AddIcon />}
                sx={{ mb: 2 }}
                variant="outlined"
                onClick={handleCreateSupplier}
              >
                {t("Add New Supplier")}
              </Button>
            </Box>
          </>
        )}
        <TableView
          loading={suppliersListQuery.loading}
          suppliers={suppliersFiltered ?? suppliers ?? []}
          onUpdate={(supplierId, supplier) => {
            suppliersListQuery.updateQuery((data) => {
              const suppliers = data?.supplier_list?.suppliers;
              if (!data?.supplier_list || !suppliers) return data;

              const index = suppliers.findIndex(
                (sup) => sup.uniqueId === supplierId
              );

              return {
                ...data,
                supplier_list: {
                  ...data.supplier_list,
                  suppliers: [
                    ...suppliers.slice(0, index),
                    { ...supplier },
                    ...suppliers.slice(index + 1),
                  ],
                },
              };
            });
          }}
        />
      </Box>
      <SupplierAddModal
        open={modal === "add_supplier"}
        onClose={() => setModal(null)}
        onSave={(newSupplier) => {
          setModal(null);

          suppliersListQuery.updateQuery((data) => {
            if (!data?.supplier_list) return data;

            return {
              ...data,
              supplier_list: {
                ...data.supplier_list,
                suppliers: [
                  newSupplier,
                  ...(data.supplier_list.suppliers ?? []),
                ],
              },
            };
          });
        }}
      />
    </>
  );
}
